import { useEffect, useMemo, useRef, useState } from 'react'
import { useMediaDevice, useMeeting } from '@videosdk.live/react-sdk'
import { LuMic, LuSettings, LuVideo } from 'react-icons/lu'
import DeviceSelect from './DeviceSelect.jsx'

/** Device list → react-select options. Deduped by deviceId, with a numbered
 * fallback label because the browser hides real labels until a mic/camera
 * permission has been granted at least once. Windows exposes the same
 * physical mic under 'default'/'communications' aliases too — those stay,
 * they are how a user picks "whatever the OS default is", same as Meet. */
function toOptions(devices, fallbackLabel) {
  const seen = new Set()
  const options = []
  ;(devices ?? []).forEach((d) => {
    if (!d?.deviceId || seen.has(d.deviceId)) return
    seen.add(d.deviceId)
    options.push({ value: d.deviceId, label: d.label?.trim() || `${fallbackLabel} ${options.length + 1}` })
  })
  return options
}

/**
 * Physical mic/camera picker for any VideoSDK call — a gear button that pops
 * a panel with a Microphone and a Camera dropdown. Must render inside a
 * <MeetingProvider>; takes no props so every call UI (education classroom,
 * astrology consultation) mounts the identical control.
 *
 * Switching goes through useMeeting's changeMic/changeWebcam — the SDK swaps
 * the published track in place, so the other side never sees a mute/unmute
 * blip. A pick made while that device is OFF is only recorded here and
 * applied the moment the device next turns on: changeMic/changeWebcam on a
 * disabled device is undefined SDK territory (nothing documents whether it
 * stores, ignores, or force-enables), so it is never called in that state.
 */
export default function DeviceSettingsButton() {
  const [open, setOpen] = useState(false)
  const [mics, setMics] = useState([])
  const [cams, setCams] = useState([])
  // The user's explicit picks ({ mic, cam } deviceIds). Kept separate from
  // the SDK's selected*Device so an off-device pick survives until it can be
  // applied, and so the dropdown always shows the user's intent.
  const [chosen, setChosen] = useState({ mic: null, cam: null })
  const rootRef = useRef(null)

  const { changeMic, changeWebcam, selectedMicrophoneDevice, selectedCameraDevice, localMicOn, localWebcamOn } =
    useMeeting()

  const refreshRef = useRef(() => {})
  // Re-lists on hardware plug/unplug while the panel is open (and harmlessly
  // when closed — the fresh list is simply ready for the next open).
  const { getCameras, getMicrophones } = useMediaDevice({ onDeviceChanged: () => refreshRef.current() })

  const refresh = async () => {
    try {
      const [micList, camList] = await Promise.all([getMicrophones(), getCameras()])
      setMics(micList ?? [])
      setCams(camList ?? [])
    } catch (err) {
      // Enumeration can fail before any permission exists — keep whatever
      // lists we already have; the numbered fallback labels cover the rest.
      console.error('[DeviceSettingsButton] device enumeration failed:', err)
    }
  }
  refreshRef.current = refresh

  // Fetch on every open, not once: labels are empty until the browser grants
  // a device permission, which usually happens after joining — reopening the
  // panel is what upgrades "Microphone 1" to the real device name.
  useEffect(() => {
    if (open) refreshRef.current()
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const applyChange = (kind, deviceId) => {
    try {
      // changeMic/changeWebcam are typed void but reject as promises on some
      // failures — swallow both shapes, the dropdown just stays put.
      Promise.resolve(kind === 'mic' ? changeMic(deviceId) : changeWebcam(deviceId)).catch((err) =>
        console.error(`[DeviceSettingsButton] ${kind} switch failed:`, err)
      )
    } catch (err) {
      console.error(`[DeviceSettingsButton] ${kind} switch failed:`, err)
    }
  }

  const pickMic = (deviceId) => {
    setChosen((c) => ({ ...c, mic: deviceId }))
    if (localMicOn) applyChange('mic', deviceId)
  }
  const pickCam = (deviceId) => {
    setChosen((c) => ({ ...c, cam: deviceId }))
    if (localWebcamOn) applyChange('cam', deviceId)
  }

  // Deferred application: a pick made while the device was off lands the
  // moment that device turns on. Guarded on the device still being attached
  // so a stale pick can never switch to unplugged hardware.
  useEffect(() => {
    if (!localMicOn || !chosen.mic) return
    if (selectedMicrophoneDevice?.deviceId === chosen.mic) return
    if (mics.some((d) => d.deviceId === chosen.mic)) applyChange('mic', chosen.mic)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localMicOn])
  useEffect(() => {
    if (!localWebcamOn || !chosen.cam) return
    if (selectedCameraDevice?.deviceId === chosen.cam) return
    if (cams.some((d) => d.deviceId === chosen.cam)) applyChange('cam', chosen.cam)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localWebcamOn])

  const micOptions = useMemo(() => toOptions(mics, 'Microphone'), [mics])
  const camOptions = useMemo(() => toOptions(cams, 'Camera'), [cams])

  const currentMicId = chosen.mic ?? selectedMicrophoneDevice?.deviceId ?? null
  const currentCamId = chosen.cam ?? selectedCameraDevice?.deviceId ?? null

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Device settings"
        aria-haspopup="true"
        aria-expanded={open}
        title="Device settings"
        className={`w-12 h-12 rounded-full flex items-center justify-center border transition duration-200 ${
          open
            ? 'bg-white/20 border-white/30 text-white'
            : 'bg-white/10 border-white/20 text-white/90 hover:bg-white/15'
        }`}
      >
        <LuSettings className="w-5 h-5" />
      </button>
      {open && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-50 w-72 max-w-[calc(100vw-2rem)] rounded-xl bg-[#171310] border border-white/15 shadow-2xl p-4 space-y-4 text-left">
          <p className="text-[11px] font-bold uppercase tracking-wider text-white/70">Device Settings</p>
          <DeviceSelect
            icon={LuMic}
            label="Microphone"
            inputId="device-settings-mic"
            options={micOptions}
            value={currentMicId}
            onChange={pickMic}
          />
          <DeviceSelect
            icon={LuVideo}
            label="Camera"
            inputId="device-settings-cam"
            options={camOptions}
            value={currentCamId}
            onChange={pickCam}
          />
        </div>
      )}
    </div>
  )
}
